// src/modules/time-tracking/MyPay.js (employee self portal)
//
// The employee's own weekly hours + pay (GET /time-tracking/me/payroll).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { FaArrowLeft, FaDollarSign, FaClock } from 'react-icons/fa';
import EmployeeLayout from '../../layouts/EmployeeLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { TableContainer, StyledTable } from '../../components/styled/CommonStyles';
import { getMyPayroll, formatHours } from './timeTrackingService';
import { formatCurrency } from '../../utils/utils';

const mondayWeekStart = (d) => startOfWeek(d, { weekStartsOn: 1 });
const sundayWeekEnd = (d) => endOfWeek(d, { weekStartsOn: 1 });
const iso = (d) => format(d, 'yyyy-MM-dd');

const PERIODS = {
  this_week: { label: 'This week', range: () => ({ from: mondayWeekStart(new Date()), to: sundayWeekEnd(new Date()) }) },
  last_week: { label: 'Last week', range: () => ({ from: mondayWeekStart(subWeeks(new Date(), 1)), to: sundayWeekEnd(subWeeks(new Date(), 1)) }) },
  last_4: { label: 'Last 4 weeks', range: () => ({ from: mondayWeekStart(subWeeks(new Date(), 3)), to: sundayWeekEnd(new Date()) }) },
};

const BackLink = styled.button`
  background: none; border: none; color: ${(p) => p.theme.colors.primary}; cursor: pointer;
  display: flex; align-items: center; gap: 6px; margin-bottom: ${(p) => p.theme.spacing(2)}; font-size: 1rem;
`;
const Chips = styled.div`display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: ${(p) => p.theme.spacing(3)};`;
const Chip = styled.button`
  padding: 8px 14px; border-radius: 20px; cursor: pointer; font-weight: 500;
  border: 1px solid ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.inputGray)};
  background: ${(p) => (p.active ? `${p.theme.colors.primary}22` : 'transparent')};
  color: ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.textSecondary)};
`;
const Summary = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: ${(p) => p.theme.spacing(2)};
  margin-bottom: ${(p) => p.theme.spacing(3)};
`;
const Tile = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; box-shadow: 0 2px 4px rgba(0,0,0,.1);
  .label { color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.85rem; display: flex; align-items: center; gap: 8px; }
  .value { color: ${(p) => p.theme.colors.primary}; font-size: 1.8rem; font-weight: 700; margin-top: 6px; }
`;
const Note = styled.div`
  color: #f39c12; font-size: 0.9rem; margin-bottom: ${(p) => p.theme.spacing(2)};
`;

const MyPay = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('this_week');
  const initial = PERIODS.this_week.range();
  const [from, setFrom] = useState(iso(initial.from));
  const [to, setTo] = useState(iso(initial.to));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyPreset = (key) => {
    setPreset(key);
    const { from: f, to: t } = PERIODS[key].range();
    setFrom(iso(f)); setTo(iso(t));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyPayroll({ from, to: `${to}T23:59:59` });
      setData(res);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || { totalSeconds: 0, pay: 0, unratedShiftCount: 0 };
  const weeks = data?.weeks || [];

  return (
    <EmployeeLayout title="My Pay">
      <BackLink onClick={() => navigate('/my-time')}><FaArrowLeft /> Back to My Time</BackLink>

      <Chips>
        {Object.entries(PERIODS).map(([key, p]) => (
          <Chip key={key} active={preset === key} onClick={() => applyPreset(key)}>{p.label}</Chip>
        ))}
      </Chips>

      {loading ? <LoadingSpinner /> : (
        <>
          <Summary>
            <Tile>
              <div className="label"><FaClock /> Hours</div>
              <div className="value">{formatHours(totals.totalSeconds)}</div>
            </Tile>
            <Tile>
              <div className="label"><FaDollarSign /> Pay</div>
              <div className="value">{formatCurrency(totals.pay || 0)}</div>
            </Tile>
          </Summary>

          {totals.unratedShiftCount > 0 && (
            <Note>Some hours have no pay rate set — contact your manager.</Note>
          )}

          <TableContainer>
            <StyledTable>
              <thead>
                <tr><th>Week</th><th>Hours</th><th>Pay</th><th>Shifts</th></tr>
              </thead>
              <tbody>
                {weeks.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>No pay in this period.</td></tr>
                ) : weeks.map((w) => (
                  <tr key={w.weekStart}>
                    <td>{format(new Date(w.weekStart), 'MMM d')} – {format(new Date(w.weekEnd), 'MMM d')}</td>
                    <td>{formatHours(w.totalSeconds)}</td>
                    <td>{formatCurrency(w.pay)}</td>
                    <td>{w.shiftCount}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </TableContainer>
        </>
      )}
    </EmployeeLayout>
  );
};

export default MyPay;
